import React, { useCallback } from 'react';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import {
  BodyText,
  Button,
  DisplayText,
  Form,
  theme,
  TitleText,
  useNoticeBannersStackContext,
  useResponsive,
} from '@sg/garnish';

import {
  GroupOrderLocationInfo,
  GroupOrderModal,
  GroupOrderModalCloseButton,
  GroupOrderModalContent,
  GroupOrderModalContentBlock,
  GroupOrderModalFooter,
  GroupOrderModalScrollView,
  useGroupOrderingContent,
} from '@order/features-group-ordering';
import { useFeatureFlag } from '@order/LaunchDarkly';

import { LoadingAnimation, NotFoundView } from '../../components';
import { useIsLoggingIn } from '../../context/AuthMachine';
import {
  useGroupOrderJoinMutation,
  useGroupOrderJoinPreviewQuery,
} from './graphql/GroupOrderJoin.generated';
import {
  checkIfDeliveryGroupOrder,
  checkIfPreviewError,
  getLocationDetails,
  getPreviewErrorMessage,
} from './helpers';
import { type GroupOrderJoinFormData, useGroupOrderJoinForm } from './hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const GroupOrderJoinScreen = () => {
  const isGroupOrderingEnabled = useFeatureFlag('CELS-5355-order-group-ordering-enabled');

  const isLoggingIn = useIsLoggingIn();

  const { joinCode } = useLocalSearchParams<GroupOrderJoinScreenParams>();

  // ─────────────────────────────────────────────────────────────────────

  if (!isGroupOrderingEnabled) {
    return <NotFoundView withoutFooter />;
  }

  if (isLoggingIn) {
    return <LoadingAnimation />;
  }

  return <GroupOrderJoinScreenReady joinCode={joinCode} />;
};

// ─────────────────────────────────────────────────────────────────────────────

const GroupOrderJoinScreenReady = (props: GroupOrderJoinScreenReadyProps) => {
  const { joinCode } = props;

  const router = useRouter();
  const { formatMessage } = useIntl();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();
  const { match } = useResponsive();
  const { mappedContent: groupOrderingContent } = useGroupOrderingContent();

  // ─── Remote Data ─────────────────────────────────────────────────────

  const [previewResponse] = useGroupOrderJoinPreviewQuery({
    variables: { joinCode },
    requestPolicy: 'network-only',
  });
  const [, joinGroupOrder] = useGroupOrderJoinMutation();

  const preview = previewResponse.data?.multipartyOrderPreview;

  // ─── Form ────────────────────────────────────────────────────────────

  const { form, maxLengths, rules } = useGroupOrderJoinForm();
  const { control, formState, watch } = form;
  const { errors, isSubmitting } = formState;

  // ─── Derived ─────────────────────────────────────────────────────────

  const previewErrorMessage = getPreviewErrorMessage({ preview });
  const hasPreviewError = checkIfPreviewError({ preview });
  const isDeliveryGroupOrder = checkIfDeliveryGroupOrder({ preview });
  const locationDetails = getLocationDetails({ preview });
  const customerName = watch('customerName');
  const isSubmitDisabled = !customerName.trim();

  // ─── Handlers ────────────────────────────────────────────────────────

  const closeModal = useCallback(() => {
    router.back();
  }, [router]);

  const onJoinOrderError = useCallback(() => {
    const text = formatMessage(messages.joinOrderError);

    addNoticeBanner(
      { id: JOIN_ERROR_BANNER_ID, text, palette: 'caution', autoHideTimeout: 1800 },
      true,
    );
  }, [addNoticeBanner, formatMessage]);

  const submitGroupOrderJoin = useCallback(
    async (formData: GroupOrderJoinFormData) => {
      const response = await joinGroupOrder({
        input: { joinCode, customerName: formData.customerName },
      });
      const result = response.data?.joinMultipartyOrder;

      if (result?.__typename !== 'JoinMultipartyOrderSuccess') {
        onJoinOrderError();

        return;
      }

      if (isDeliveryGroupOrder) {
        const addressId = result.order.deliveryOrderDetail?.address?.id;

        if (!addressId) {
          onJoinOrderError();

          return;
        }

        router.dismissTo({ pathname: '/delivery/[addressId]/menu', params: { addressId } });

        return;
      }

      const restaurantSlug = result.order.restaurant.slug;

      router.dismissTo({ pathname: '/[restaurantSlug]/menu', params: { restaurantSlug } });
    },
    [isDeliveryGroupOrder, joinCode, joinGroupOrder, onJoinOrderError, router],
  );

  // ─── Loading State ───────────────────────────────────────────────────

  if (previewResponse.fetching) {
    return (
      <GroupOrderModal onClose={closeModal}>
        <View style={styles.loadingContainer}>
          <LoadingAnimation size={match(['large', 'small'])} />
        </View>
      </GroupOrderModal>
    );
  }

  // ─── Error States ────────────────────────────────────────────────────

  if (previewResponse.error || hasPreviewError) {
    return (
      <GroupOrderModal onClose={closeModal}>
        <GroupOrderModalContent>
          <GroupOrderModalScrollView contentContainerStyle={styles.errorScrollContent}>
            <View style={styles.errorContainer}>
              <GroupOrderModalContentBlock>
                <GroupOrderModalCloseButton
                  accessibilityLabel={formatMessage(messages.closeA11yLabel)}
                  onPress={closeModal}
                />
              </GroupOrderModalContentBlock>

              <View style={styles.errorBody}>
                <GroupOrderModalContentBlock style={styles.errorIllustrationSection}>
                  <View style={styles.errorIllustration}>
                    <NotFoundView.Image />
                  </View>
                </GroupOrderModalContentBlock>

                <GroupOrderModalContentBlock style={styles.errorHeader}>
                  <DisplayText sizeMatch={['32']}>
                    <FormattedMessage {...messages.errorTitle} />
                  </DisplayText>

                  <BodyText sizeMatch={['20']} style={[styles.supportingText, styles.textCenter]}>
                    {previewErrorMessage ? previewErrorMessage : null}

                    {!previewErrorMessage && preview?.__typename === 'OrderLocked' ? (
                      <FormattedMessage {...messages.lockedOrderError} />
                    ) : undefined}

                    {!previewErrorMessage && !hasPreviewError ? (
                      <FormattedMessage {...messages.previewError} />
                    ) : undefined}
                  </BodyText>
                </GroupOrderModalContentBlock>
              </View>
            </View>
          </GroupOrderModalScrollView>

          <GroupOrderModalFooter>
            <Button size="large-wide" onPress={closeModal}>
              <FormattedMessage {...messages.goBackCta} />
            </Button>
          </GroupOrderModalFooter>
        </GroupOrderModalContent>
      </GroupOrderModal>
    );
  }

  if (preview?.__typename !== 'MultipartyOrderPreviewSuccess') {
    return (
      <NotFoundView withoutFooter description={formatMessage(messages.invalidGroupOrderLink)} />
    );
  }

  // ─── Ready State ─────────────────────────────────────────────────────

  return (
    <GroupOrderModal onClose={closeModal}>
      <GroupOrderModalContent>
        <GroupOrderModalScrollView>
          <View style={styles.container}>
            <GroupOrderModalContentBlock>
              <GroupOrderModalCloseButton
                accessibilityLabel={formatMessage(messages.closeA11yLabel)}
                onPress={closeModal}
              />
            </GroupOrderModalContentBlock>

            <GroupOrderModalContentBlock style={styles.header}>
              <TitleText sizeMatch={['24']}>
                <FormattedMessage {...messages.title} />
              </TitleText>

              <DisplayText sizeMatch={['24']}>
                <FormattedMessage
                  {...messages.groupOrderName}
                  values={{ host_name: preview.hostDisplayName }}
                />
              </DisplayText>
            </GroupOrderModalContentBlock>

            <GroupOrderModalContentBlock style={styles.section}>
              <BodyText sizeMatch={['16']} bold>
                <FormattedMessage
                  {...messages.locationTitle}
                  values={{ channel: locationDetails.orderChannel }}
                />
              </BodyText>

              <GroupOrderLocationInfo
                orderChannel={locationDetails.orderChannel}
                label={locationDetails.label}
                address={locationDetails.address}
                secondaryAddress={locationDetails.secondaryAddress}
              />
            </GroupOrderModalContentBlock>

            <GroupOrderModalContentBlock style={styles.formSection}>
              <Form.TextFieldWithBackground
                name="customerName"
                control={control}
                rules={rules.customerName}
                label={formatMessage(messages.nameLabel)}
                placeholder={groupOrderingContent.nameFieldPlaceholder}
                notice={errors.customerName?.message}
                maxLength={maxLengths.customerName}
                hideSubmitButton
                disabled={isSubmitting}
                autoCapitalize="words"
                clearButtonType="icon"
              />
            </GroupOrderModalContentBlock>
          </View>
        </GroupOrderModalScrollView>

        <GroupOrderModalFooter>
          <Button
            size="large-wide"
            disabled={isSubmitDisabled}
            isLoading={isSubmitting}
            onPress={form.handleSubmit(submitGroupOrderJoin)}
          >
            <FormattedMessage {...messages.submitCta} />
          </Button>
        </GroupOrderModalFooter>
      </GroupOrderModalContent>
    </GroupOrderModal>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  title: {
    defaultMessage: "You've been invited to join:",
    description: 'Group Order Join | Title',
  },
  groupOrderName: {
    defaultMessage: "{host_name}'s group order",
    description: 'Group Order Join | Group order name',
  },
  closeA11yLabel: {
    defaultMessage: 'Close group order invitation',
    description: 'Group Order Join | Close button accessibility label',
  },
  locationTitle: {
    defaultMessage:
      '{channel, select, delivery {Delivery location} outpost {Outpost location} pickup {Pickup location} other {Location}}',
    description: 'Group Order Join | Location section title',
  },
  nameLabel: {
    defaultMessage: 'Name',
    description: 'Group Order Join | Name field label',
  },
  submitCta: {
    defaultMessage: 'Join group order',
    description: 'Group Order Join | Submit CTA',
  },
  errorTitle: {
    defaultMessage: 'Unable to join group order',
    description: 'Group Order Join | Error title',
  },
  previewError: {
    defaultMessage: 'This group order invitation is unavailable.',
    description: 'Group Order Join | Preview error message',
  },
  invalidGroupOrderLink: {
    defaultMessage: 'Please ensure your group order link is valid and try again.',
    description: 'Group Order Join | Invalid link error message',
  },
  lockedOrderError: {
    defaultMessage: 'This group order is no longer accepting guests.',
    description: 'Group Order Join | Locked order error message',
  },
  joinOrderError: {
    defaultMessage: 'Unable to join group order',
    description: 'Group Order Join | Join order error banner',
  },
  goBackCta: {
    defaultMessage: 'Go back',
    description: 'Group Order Join | Error go back CTA',
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const JOIN_ERROR_BANNER_ID = 'group-order-join-error-banner';

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    paddingTop: theme.spacing['6'],
    paddingBottom: theme.spacing['6'],
  },
  errorContainer: {
    flex: 1,
    paddingTop: theme.spacing['6'],
    paddingBottom: theme.spacing['6'],
  },
  errorBody: {
    flex: 1,
    justifyContent: 'center',
  },
  errorScrollContent: {
    flexGrow: 1,
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 400,
  },
  errorHeader: {
    alignItems: 'center',
    gap: theme.spacing['2'],
    paddingTop: theme.spacing['3'],
    paddingBottom: theme.spacing['6'],
  },
  errorIllustration: {
    alignItems: 'center',
  },
  errorIllustrationSection: {
    marginBottom: theme.spacing['4'],
  },
  header: {
    paddingTop: theme.spacing['3'],
    paddingBottom: theme.spacing['6'],
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.GRAY,
  },
  section: {
    gap: theme.spacing['2'],
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.GRAY,
    paddingVertical: theme.spacing['6'],
  },
  formSection: {
    gap: theme.spacing['4'],
    paddingTop: theme.spacing['8'],
  },
  supportingText: {
    color: theme.colors.CHARCOAL,
  },
  textCenter: {
    textAlign: 'center',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type GroupOrderJoinScreenParams = {
  joinCode: string;
};

type GroupOrderJoinScreenReadyProps = {
  joinCode: string;
};
